import { Box, styled } from '@mui/material';
import { BizlyThemeOverride } from 'ThemedApp';
import { SpinnerOverlay } from 'components/Spinner';
import { SlotProvider } from 'components/ui/SlotSystem';
import { ROUTES } from 'constants/routes';
import { RICOH_TEAM_ID } from 'constants/teamId';
import { decodeRedirectUrl } from 'pages/Login/util';
import { PageNotFound } from 'pages/PageNotFound';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import queryString from 'query-string';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { PostHogFeatureFlags } from 'shared';
import { useUserContext } from 'stores/user';
import { isBizlyAdmin } from 'utils/adminPrivileges';
import { takeFirstIfArray } from '../util';
import { V2RsvpRedirect } from './V2RsvpRedirect';

// Lazy-loaded page components for code splitting
const Dashboard = lazy(() => import('pages/Dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const Events = lazy(() => import('pages/Events/Events').then(m => ({ default: m.Events })));
const Event = lazy(() => import('pages/Event/Event').then(m => ({ default: m.Event })));
const Profile = lazy(() => import('pages/Profile').then(m => ({ default: m.Profile })));
const VenuesPage = lazy(() => import('pages/Venues/VenuesPage').then(m => ({ default: m.VenuesPage })));
const FavoriteVenues = lazy(() => import('pages/FavoriteVenues'));
const ExploreCities = lazy(() => import('pages/ExploreCities'));
const TeamSettings = lazy(() => import('pages/TeamSettings').then(m => ({ default: m.TeamSettings })));
const Reporting = lazy(() => import('pages/Reporting').then(m => ({ default: m.Reporting })));
const LeadVolume = lazy(() => import('pages/LeadVolume').then(m => ({ default: m.LeadVolume })));
const Approvals = lazy(() => import('pages/Approvals').then(m => ({ default: m.Approvals })));
const RSVP = lazy(() => import('pages/RSVP'));
const TemplatesRoute = lazy(() => import('./Templates'));
const Authentication = lazy(() =>
    import('pages/Authentication/Authentication').then(m => ({ default: m.Authentication }))
);

const ResponsiveBox = styled(Box)(({ theme }) => ({
    height: '100%',
    minWidth: 'auto',

    [theme.breakpoints.up('lg')]: {
        minWidth: '1200px',
    },
}));

const RespectfulRedirect = () => {
    const location = useLocation();
    const parsed = queryString.parse(location.search);
    const { redirect } = takeFirstIfArray(parsed as { [param: string]: string | string[] | null | undefined });

    return <Navigate to={redirect ? decodeRedirectUrl(redirect) : '/'} />;
};

// The authenticated equivalent of DiscoverExplorePage — same VenueDetails dialog, same
// venueId/preview query params via useVenueDialog — so a shared venue link built with
// getDiscoverPaths().explore still opens the right venue for a signed-in recipient instead
// of losing the query string to the blanket `/discover/*` -> `/` redirect below.
const DiscoverExploreRedirect = () => {
    const location = useLocation();
    return <Navigate to={{ pathname: '/explore-venues', search: location.search }} replace />;
};

export const AuthBlockPassed = () => {
    const { user, isTeamAdmin } = useUserContext();
    const location = useLocation();
    const dashboardEnabled = useFeatureFlagEnabled(PostHogFeatureFlags.dashboardPage);

    if (user.platformRole === 'venue') {
        return <Navigate to="/os" replace />;
    }

    // A magic-link token must still reach useAuthenticate even when already logged in — otherwise
    // RespectfulRedirect below swallows the token unused and the current session never gets a
    // chance to switch (or re-confirm) accounts.
    const { token: magicToken } = takeFirstIfArray(
        queryString.parse(location.search) as { [param: string]: string | string[] | null | undefined }
    );
    const shouldRedirectAwayFromAuth = Boolean(user.email) && !magicToken;

    return (
        <SlotProvider>
            <ResponsiveBox key={user.team?.id}>
                <Suspense fallback={<SpinnerOverlay />}>
                    <Routes>
                        {/* Authentication & Profile Routes */}
                        <Route
                            path="/login/*"
                            element={
                                shouldRedirectAwayFromAuth ? <RespectfulRedirect /> : <Authentication page="sign-in" />
                            }
                        />
                        <Route
                            path="/sign-in/*"
                            element={
                                shouldRedirectAwayFromAuth ? <RespectfulRedirect /> : <Authentication page="sign-in" />
                            }
                        />
                        <Route
                            path="/sign-up/*"
                            element={
                                shouldRedirectAwayFromAuth ? <RespectfulRedirect /> : <Authentication page="sign-up" />
                            }
                        />
                        <Route path={ROUTES.PROFILE + '/*'} element={<Profile />} />

                        {/* Dashboard as Home */}
                        {dashboardEnabled ? (
                            <Route path="/" element={<Dashboard />} />
                        ) : (
                            <Route path="/" element={<Navigate to="/events" replace />} />
                        )}

                        {/* Event-related Routes */}
                        <Route path="/events/" element={<Events />} />
                        <Route path={ROUTES.EVENT + '/:eventId/*'} element={<Event />} />
                        <Route
                            path="/events/:eventId/rsvp"
                            element={
                                <BizlyThemeOverride>
                                    <RSVP />
                                </BizlyThemeOverride>
                            }
                        />
                        <Route
                            path="/v2/events/:eventId/rsvp"
                            element={
                                <BizlyThemeOverride>
                                    <V2RsvpRedirect />
                                </BizlyThemeOverride>
                            }
                        />

                        {/* Venues */}
                        <Route path="/explore-venues/*" element={<VenuesPage />} />
                        <Route path="/favorite-venues" element={<FavoriteVenues />} />
                        <Route path="/explore-cities/*" element={<ExploreCities />} />

                        {/* Discover pre-experience — authenticated users redirected to the
                            equivalent in-app page. explore-venues preserves the query string
                            (see DiscoverExploreRedirect); anything else falls back to the dashboard. */}
                        <Route path="/discover/explore-venues/*" element={<DiscoverExploreRedirect />} />
                        <Route path="/discover/*" element={<Navigate to="/" replace />} />

                        {/* Admin */}
                        <Route path={ROUTES.TEAM_SETTINGS + '/*'} element={<TeamSettings />} />
                        <Route
                            path="/approvals"
                            element={
                                isTeamAdmin && user.team?.id === RICOH_TEAM_ID ? (
                                    <Approvals />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            }
                        />

                        {/* Templates & Playbooks */}
                        <Route path="/explore-templates/*" element={<TemplatesRoute />} />

                        {/* Reporting */}
                        <Route path="/reporting" element={<Reporting />} />
                        <Route
                            path="/lead-volume"
                            element={isBizlyAdmin(user) ? <LeadVolume /> : <Navigate to="/" replace />}
                        />

                        {/* Fallback Routes */}
                        <Route path="*" element={<PageNotFound />} />
                    </Routes>
                </Suspense>
            </ResponsiveBox>
        </SlotProvider>
    );
};
